
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, UploadCloud, X, Image, Zap } from "lucide-react";
import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";


interface BrandingSettingsProps {
  settings: any;
  onSettingsChange: (settings: any) => void;
}

const BrandingSettings = ({ settings, onSettingsChange }: BrandingSettingsProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) {
      return;
    }
    const file = acceptedFiles[0];
    if (file.size > 2 * 1024 * 1024) { // 2MB limit
      toast({ title: "File too large", description: "Please upload an image smaller than 2MB.", variant: "destructive" });
      return;
    }
    
    setIsUploading(true);
    try {
      const fileName = `${Date.now()}-${file.name}`;
      const { data, error } = await supabase.storage
        .from('logos')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) throw error;
      
      const { data: { publicUrl } } = supabase.storage.from('logos').getPublicUrl(data.path);
      onSettingsChange({ logoUrl: publicUrl });
      toast({ title: "Success", description: "Logo uploaded successfully." });

    } catch (error: any) {
      toast({ title: "Upload failed", description: error.message, variant: "destructive" });
    } finally {
      setIsUploading(false);
    }
  }, [onSettingsChange, toast]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.svg'] },
    multiple: false,
  });

  const handleRemoveLogo = () => {
    onSettingsChange({ logoUrl: '' });
    toast({ title: "Logo removed", description: "Don't forget to save your changes." });
  }
  
  const fontClass = {
    'Poppins': 'font-poppins',
    'Playfair Display': 'font-playfair',
    'Montserrat': 'font-montserrat',
    'Inter': 'font-sans'
  }[settings.logoFontFamily || 'Poppins'] || 'font-sans';

  return (
    <Card className="admin-card">
      <CardHeader>
        <CardTitle className="text-foreground">Branding & Logo</CardTitle>
        <CardDescription className="text-muted-foreground">
          Customize your site's logo and branding.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
            <Label htmlFor="logoUpload">Custom Logo</Label>
            <div className="flex items-center gap-4">
              <div className="w-24 h-24 rounded-md border flex items-center justify-center bg-muted/40">
                {settings.logoUrl ? <img src={settings.logoUrl} alt="Logo preview" className="max-w-full max-h-full object-contain" /> : <Image className="w-10 h-10 text-muted-foreground"/>}
              </div>
              <div className="flex-1">
                <div {...getRootProps()} className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer ${isDragActive ? 'border-blue-500 bg-blue-50' : 'border-border'}`}>
                  <input {...getInputProps()} id="logoUpload" />
                  {isUploading ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                      <p>Uploading...</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <UploadCloud className="h-8 w-8 text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        {isDragActive ? "Drop the image here" : "Drag 'n' drop an image here, or click to select"}
                      </p>
                      <p className="text-xs text-muted-foreground">PNG, JPG, GIF, SVG up to 2MB</p>
                    </div>
                  )}
                </div>
                 {settings.logoUrl && (
                  <Button variant="link" size="sm" className="text-destructive mt-2" onClick={handleRemoveLogo}>
                    <X className="w-4 h-4 mr-1"/> Remove Logo
                  </Button>
                )}
              </div>
            </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="logoText">Logo Text</Label>
          <Input 
            id="logoText"
            value={settings.logoText || ''}
            onChange={(e) => onSettingsChange({ logoText: e.target.value })}
            placeholder="Your Site Name"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="logoFontFamily">Logo Font Family</Label>
            <Select
              value={settings.logoFontFamily || 'Poppins'}
              onValueChange={(value) => onSettingsChange({ logoFontFamily: value })}
            >
              <SelectTrigger id="logoFontFamily">
                <SelectValue placeholder="Select a font" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Inter">Inter (Default)</SelectItem>
                <SelectItem value="Poppins">Poppins</SelectItem>
                <SelectItem value="Playfair Display">Playfair Display</SelectItem>
                <SelectItem value="Montserrat">Montserrat</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="logoFontWeight">Logo Font Weight</Label>
            <Select
              value={String(settings.logoFontWeight || 600)}
              onValueChange={(value) => onSettingsChange({ logoFontWeight: Number(value) })}
            >
              <SelectTrigger id="logoFontWeight">
                <SelectValue placeholder="Select weight" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="300">Light</SelectItem>
                <SelectItem value="400">Regular</SelectItem>
                <SelectItem value="500">Medium</SelectItem>
                <SelectItem value="600">Semi-Bold</SelectItem>
                <SelectItem value="700">Bold</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
            <Label>Logo Preview</Label>
            <div className="p-4 rounded-lg border bg-muted/20 flex items-center justify-center">
              <div className="flex items-center space-x-2">
                {settings.logoUrl ? (
                    <img src={settings.logoUrl} alt={settings.logoText || 'Logo'} className="h-8 w-auto max-h-8" />
                ) : (
                    settings.showLogoIcon !== false && (
                    <div className="h-8 w-8 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center shadow">
                        <Zap className="h-5 w-5 text-primary-foreground" />
                    </div>
                    )
                )}
                {settings.showLogoText !== false && (
                    <span 
                        className={`text-xl text-foreground tracking-wider ${fontClass}`}
                        style={{ fontWeight: settings.logoFontWeight || 600 }}
                    >
                        {settings.logoText || 'VRAM'}
                    </span>
                )}
              </div>
            </div>
        </div>

        <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-sm font-medium text-foreground">Show Icon with Logo</Label>
              <p className="text-xs text-muted-foreground">
                Toggles the default icon visibility when no custom logo is uploaded.
              </p>
            </div>
            <Switch
              checked={settings.showLogoIcon !== false}
              onCheckedChange={(checked) => onSettingsChange({ showLogoIcon: checked })}
              disabled={!!settings.logoUrl}
            />
        </div>
        
        <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label className="text-sm font-medium text-foreground">Show Text with Logo</Label>
              <p className="text-xs text-muted-foreground">
                Toggles the logo text visibility.
              </p>
            </div>
            <Switch
              checked={settings.showLogoText !== false}
              onCheckedChange={(checked) => onSettingsChange({ showLogoText: checked })}
            />
        </div>
      </CardContent>
    </Card>
  );
};

export default BrandingSettings;

